import React, { useState } from 'react';
import {
  LayoutDashboard,
  Grid3x3,
  Sidebar,
  Maximize2,
  Monitor,
  Smartphone,
  Tablet,
  Eye,
  Heart,
  Activity,
  Bell,
  Search,
  Plus,
  Filter,
  ArrowRight,
  Zap,
  Brain,
  Target,
  TrendingUp,
  Calendar,
  Pill,
  Utensils,
  Shield,
  Settings,
  User
} from 'lucide-react';
import LayoutImplementationGuide from './LayoutImplementationGuide';
import NavigationHeader from '../common/NavigationHeader';

interface LayoutOption {
  id: string;
  name: string;
  description: string;
  benefits: string[];
  preview: React.ReactNode;
  features: string[];
  bestFor: string[];
}

interface LayoutOptionsProps {
  onBackToApp?: () => void;
}

const LayoutOptions: React.FC<LayoutOptionsProps> = ({ onBackToApp }) => {
  const [selectedLayout, setSelectedLayout] = useState<string>('cards-first');
  const [deviceView, setDeviceView] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  const layoutOptions: LayoutOption[] = [
    {
      id: 'cards-first',
      name: 'Health Cards Dashboard',
      description: 'Card-based layout prioritizing key health metrics and quick actions',
      benefits: [
        'Immediate visual access to critical health data',
        'Reduced cognitive load with focused information cards',
        'Quick action buttons for common tasks',
        'AI-powered insights prominently displayed'
      ],
      features: [
        'Priority health metrics at top',
        'AI insights card with actionable recommendations',
        'Quick capture buttons (glucose, meal, symptoms)',
        'Contextual alerts and notifications',
        'Progress tracking widgets'
      ],
      bestFor: [
        'Patients managing chronic conditions',
        'Users who need quick data entry',
        'People who prefer visual over text-heavy interfaces'
      ],
      preview: (
        <div className="space-y-4 p-4 bg-slate-50 rounded-lg h-64 overflow-hidden">
          {/* Priority Cards Row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <Heart className="h-4 w-4 text-red-500 mb-1" />
              <div className="text-xs font-medium">Glucose</div>
              <div className="text-lg font-bold text-red-600">120</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Activity className="h-4 w-4 text-blue-500 mb-1" />
              <div className="text-xs font-medium">BP</div>
              <div className="text-lg font-bold text-blue-600">120/80</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <Target className="h-4 w-4 text-green-500 mb-1" />
              <div className="text-xs font-medium">Steps</div>
              <div className="text-lg font-bold text-green-600">8,492</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <Brain className="h-4 w-4 text-purple-500 mb-1" />
              <div className="text-xs font-medium">AI Score</div>
              <div className="text-lg font-bold text-purple-600">85%</div>
            </div>
          </div>
          
          {/* AI Insights Card */}
          <div className="bg-gradient-to-r from-zippy-50 to-orange-50 border border-zippy-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Brain className="h-4 w-4 text-zippy-600" />
              <span className="text-sm font-medium text-zippy-800">AI Health Insight</span>
            </div>
            <p className="text-xs text-zippy-700">Your glucose levels are trending higher after 6 PM. Consider logging dinner details for personalized recommendations.</p>
            <button className="mt-2 text-xs bg-zippy-600 text-white px-2 py-1 rounded">View Analysis</button>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-2">
            <button className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <Plus className="h-4 w-4 mx-auto mb-1 text-slate-600" />
              <span className="text-xs">Log Meal</span>
            </button>
            <button className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <Pill className="h-4 w-4 mx-auto mb-1 text-slate-600" />
              <span className="text-xs">Medication</span>
            </button>
            <button className="bg-white border border-slate-200 rounded-lg p-2 text-center">
              <Activity className="h-4 w-4 mx-auto mb-1 text-slate-600" />
              <span className="text-xs">Symptoms</span>
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'workflow-based',
      name: 'Workflow-Centric Interface',
      description: 'Task-oriented layout that guides users through their daily health management workflows',
      benefits: [
        'Reduces decision fatigue with guided workflows',
        'Contextual information appears when needed',
        'Progressive disclosure prevents overwhelming users',
        'Intelligent task prioritization'
      ],
      features: [
        'Daily health checklist with smart ordering',
        'Contextual data entry forms',
        'Progress tracking for long-term goals',
        'Intelligent reminders and nudges',
        'Streamlined guru consultation workflow'
      ],
      bestFor: [
        'New users learning health management',
        'Users with complex medication regimens',
        'People working with health coaches/gurus'
      ],
      preview: (
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg h-64 overflow-hidden">
          {/* Today's Priority Tasks */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-800">Today's Health Tasks</span>
              <span className="text-xs text-slate-500">3 of 5 complete</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <span className="text-xs text-slate-600 line-through">Morning medication taken</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
                <span className="text-xs text-slate-600 line-through">Blood glucose logged</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-blue-500 rounded-full"></div>
                <span className="text-xs text-blue-600 font-medium">Log lunch (recommended)</span>
                <ArrowRight className="h-3 w-3 text-blue-500" />
              </div>
            </div>
          </div>
          
          {/* Contextual Guidance */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Zap className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-800">Smart Reminder</span>
            </div>
            <p className="text-xs text-blue-700">Your glucose tends to spike after lunch. Consider logging your meal for personalized portion guidance.</p>
          </div>
          
          {/* Guru Connection */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-medium text-purple-800">Your Health Guru</span>
                <p className="text-xs text-purple-600">Dr. Sarah Chen</p>
              </div>
              <button className="text-xs bg-purple-600 text-white px-2 py-1 rounded">Connect</button>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'data-rich',
      name: 'Data-Rich Analytics View',
      description: 'Comprehensive dashboard for users who want detailed insights and trends',
      benefits: [
        'Comprehensive health picture at a glance',
        'Advanced analytics and correlations',
        'Professional-grade reporting features',
        'Customizable data visualization'
      ],
      features: [
        'Multi-metric correlation charts',
        'Trend analysis with AI predictions',
        'Customizable dashboard widgets',
        'Export capabilities for healthcare providers',
        'Advanced filtering and time-range selection'
      ],
      bestFor: [
        'Healthcare providers reviewing patient data',
        'Data-savvy patients tracking multiple conditions',
        'Users preparing for medical appointments'
      ],
      preview: (
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg h-64 overflow-hidden">
          {/* Header with filters */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-800">Health Analytics</span>
            <div className="flex items-center space-x-2">
              <Filter className="h-3 w-3 text-slate-500" />
              <span className="text-xs text-slate-500">Last 30 days</span>
            </div>
          </div>
          
          {/* Charts Row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs font-medium text-slate-700 mb-2">Glucose Trends</div>
              <div className="h-16 bg-gradient-to-r from-red-100 to-red-200 rounded relative">
                <div className="absolute inset-0 flex items-end justify-between px-1 pb-1">
                  <div className="w-1 bg-red-500 rounded-t" style={{height: '60%'}}></div>
                  <div className="w-1 bg-red-500 rounded-t" style={{height: '80%'}}></div>
                  <div className="w-1 bg-red-500 rounded-t" style={{height: '40%'}}></div>
                  <div className="w-1 bg-red-500 rounded-t" style={{height: '90%'}}></div>
                  <div className="w-1 bg-red-500 rounded-t" style={{height: '70%'}}></div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Avg: 125 mg/dL</div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-xs font-medium text-slate-700 mb-2">Activity Correlation</div>
              <div className="h-16 bg-gradient-to-r from-green-100 to-green-200 rounded relative">
                <div className="absolute inset-0 flex items-end justify-between px-1 pb-1">
                  <div className="w-1 bg-green-500 rounded-t" style={{height: '40%'}}></div>
                  <div className="w-1 bg-green-500 rounded-t" style={{height: '60%'}}></div>
                  <div className="w-1 bg-green-500 rounded-t" style={{height: '80%'}}></div>
                  <div className="w-1 bg-green-500 rounded-t" style={{height: '50%'}}></div>
                  <div className="w-1 bg-green-500 rounded-t" style={{height: '70%'}}></div>
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Steps: 7,234/day</div>
            </div>
          </div>
          
          {/* Insights */}
          <div className="bg-white rounded-lg p-3 border border-slate-200">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="h-4 w-4 text-blue-500" />
              <span className="text-xs font-medium text-slate-800">Key Insights</span>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-600">• 23% improvement in glucose stability</div>
              <div className="text-xs text-slate-600">• Strong correlation between steps and sleep quality</div>
              <div className="text-xs text-slate-600">• Medication adherence: 94% (excellent)</div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentLayout = layoutOptions.find(layout => layout.id === selectedLayout);

  const getDeviceClass = () => {
    switch (deviceView) {
      case 'mobile': return 'w-full max-w-sm mx-auto';
      case 'tablet': return 'w-full max-w-2xl mx-auto';
      default: return 'w-full';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {onBackToApp && (
        <NavigationHeader 
          title="Layout Options"
          subtitle="Explore different layout approaches for better UX"
          onBackToApp={onBackToApp}
        />
      )}
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            ZippyHealth.io Layout Options
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8">
            Explore different layout approaches optimized for various user needs and workflows.
            Each design prioritizes different aspects of the health management experience.
          </p>
          <LayoutImplementationGuide />
        </div>

        {/* Layout Selector */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {layoutOptions.map((layout) => (
              <button
                key={layout.id}
                onClick={() => setSelectedLayout(layout.id)}
                className={`px-6 py-3 rounded-lg border-2 transition-all duration-200 ${
                  selectedLayout === layout.id
                    ? 'border-zippy-500 bg-zippy-50 text-zippy-800'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <div className="text-left">
                  <div className="font-medium">{layout.name}</div>
                  <div className="text-xs opacity-75">{layout.description}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Device View Selector */}
          <div className="flex justify-center items-center space-x-4 mb-6">
            <span className="text-sm text-slate-600">View as:</span>
            <div className="flex space-x-2">
              {[
                { id: 'desktop', icon: Monitor, label: 'Desktop' },
                { id: 'tablet', icon: Tablet, label: 'Tablet' },
                { id: 'mobile', icon: Smartphone, label: 'Mobile' }
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setDeviceView(id as any)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                    deviceView === id
                      ? 'bg-zippy-100 text-zippy-700'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Current Layout Details */}
        {currentLayout && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Preview */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-800">{currentLayout.name}</h2>
                  <Eye className="h-5 w-5 text-slate-500" />
                </div>
                <div className={getDeviceClass()}>
                  {currentLayout.preview}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-6">
              {/* Benefits */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Benefits</h3>
                <ul className="space-y-2">
                  {currentLayout.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-slate-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Features */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Features</h3>
                <ul className="space-y-2">
                  {currentLayout.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Zap className="h-4 w-4 text-zippy-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Best For */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Best For</h3>
                <ul className="space-y-2">
                  {currentLayout.bestFor.map((userType, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <User className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{userType}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Implementation Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-800">Non-Destructive Preview</span>
          </div>
          <p className="text-sm text-blue-700">
            These layout options are safe to explore and don't affect your current dashboard.
            You can access this preview anytime through the sidebar under "Design Preview".
          </p>
        </div>
      </div>
    </div>
  );
};

export default LayoutOptions; 
